import BootstrapModal from "bootstrap/js/dist/modal";
import { parseISO } from "date-fns";

import { UnshadowedElement, html } from "../lib/unshadowed.js";
import { UserAvatar } from "./user-avatar.js";
import { MealDateRow } from "./meal-date-row.js";
import { TextRevealer } from "./text-revealer.js";
import { AreMonthsEqual, IsDateInPast, IsoDateFormatter } from "../lib/date-utilities.js";
import { WidthObserver } from "../lib/width-observer.js";

UserAvatar.Register();
MealDateRow.Register();
TextRevealer.Register();

/**
* Get the proper edit / message buttons for a date, depending on admin status etc
* Used both for meal-date-row, and the mobile detail of calendar-display
* @param trainDate an nMealTrainDate
* */
function GetActionButtons(trainDate) {
    return html`
        <a class="button button-rectangle button-outline message-button" href="/trains/${window.CurrentTrain.Id}/message/${trainDate.AssignedUser.Id}">
            <svg role="img" width="24" height="24" aria-label="Message"><use xlink:href="#envelope-blue" /></svg><span class="text">Message</span>
        </a>
        <a class="button button-rectangle button-blue edit-own-button" href="/trains/${window.CurrentTrain.Id}/volunteer/${trainDate.Id}">
            <svg role="img" width="24" height="24" aria-label="Edit"><use xlink:href="#edit-white" /></svg><span class="text">Edit</span>
        </a>
        <a class="button button-rectangle button-outline edit-button" href="/trains/${window.CurrentTrain.Id}/volunteer/${trainDate.Id}">
            <svg role="img" width="24" height="24" aria-label="Edit"><use xlink:href="#edit-blue" /></svg><span class="text">Edit</span>
        </a>`;
}

export class CalendarDisplay extends UnshadowedElement {
    static elementName() { return "calendar-display" }

    static properties = {
        // All dates ever for the train, past and future. Array of nAccessibleEntity<nMealTrainDate>
        dates: { type: Array },
        // Start date of the current month
        startDate: { type: Date },
        // On mobile, day object currently selected
        selectedDay: { type: Object },
        // If we are showing the Meal Details modal, this is the day to display
        showingModalDay: { type: Object },
    };

    constructor() {
        super();
        this.changeMonthToday();
        this.selectedDay = undefined;
        this.showingModalDay = undefined;
    }

    changeMonth(future) {
        const newMonth = new Date(this.startDate);
        newMonth.setMonth(newMonth.getMonth() + (future ? 1 : -1));
        this.startDate = newMonth;
        this.selectedDay = undefined;
    }

    changeMonthToday() {
        const thisMonth = new Date();
        thisMonth.setHours(0, 0, 0, 0);
        thisMonth.setDate(1);
        this.startDate = thisMonth;
    }

    hasEverScrolled = false;

    selectDayMobile(day) {
        this.selectedDay = day;
        // The first selection should scroll the info box into view
        if (!this.hasEverScrolled) {
            this.hasEverScrolled = true;
            // Give a moment to update the component for proper sizing
            setTimeout(() => {
                const infoBox = this.querySelector(".meal-calendar-info-box");
                if (infoBox) infoBox.scrollIntoView(false);
            }, 100);
        }
    }

    closeMobileDetail() {
        // Save a ref to the button we got here from
        const dayButton = this.querySelector(`[data-day-of-month="${this.selectedDay.daynum}"] button`);
        // Hide the info box
        this.selectedDay = undefined;
        // Focus the button
        if (dayButton) dayButton.focus();
    }

    /**
     * Conditionally create mobile content for each day
     * Broken out because it gets a little complicated
     */
    getMobileDayContent(day) {
        if (day.status === "booked" || day.status === "partial-booked" || (!day.isPast && day.status === "available")) {
            return html`<button @click=${() => { this.selectDayMobile(day); }} class="mobile-holder"></button>`;
        } else {
            return html`<div class="mobile-holder"></div>`;
        }
    }

    /**
     * Conditionally create desktop content for each day
     * Broken out because it gets a little complicated
     */
    getDesktopDayContent(day) {
        const MAX_ENTRIES_PER_DAY = 4;
        const nodes = [];
        for (const [index, schedule] of day.scheduledDates.entries()) {
            const status = (schedule.Entity && schedule.Entity.AssignedUser && schedule.Entity.AssignedUser.Id) ? "booked" : "available";
            // If we're on the 3rd entry for a day and there are more to come, put an "x more" entry and finish the day
            if (index === MAX_ENTRIES_PER_DAY - 1 && day.scheduledDates.length > MAX_ENTRIES_PER_DAY) {
                nodes.push(html`<li>+${day.scheduledDates.length - MAX_ENTRIES_PER_DAY + 1} more</li>`);
                break;
            } else if (window.CurrentTrain.IsPlus) {
                if (!day.isPast || status === "booked") {
                    nodes.push(html`<li data-status=${status}><span class="category">${schedule.Entity.CategoryName}: </span>${schedule.Entity.Title}</li>`);
                }
            } else {
                // Non-plus, just one node
                if (status === "booked") {
                    nodes.push(html`<li>${schedule.Entity.Description}</li>`);
                } else if (day.status === "available" && !day.isPast) {
                    nodes.push(html`<li>Available</li>`);
                }
            }
        }
        if (nodes.length) {
            if (window.CurrentTrain.IsPlus || day.status === "booked") {
                // Entire day is a button containing the list of nodes
                return html`<button @click=${() => { this.showingModalDay = day; }} class="desktop-holder"><ul>${nodes}</ul></button>`;
            } else {
                // Direct page link, only for non-plus non-booked
                return html`<a href="/trains/${window.CurrentTrain.Id}/volunteer/${day.scheduledDates[0].Entity.Id}" class="desktop-holder"><ul><li>Available</li></ul></a>`;
            }
        } else {
            // Empty day
            return html`<div class="desktop-holder"></div>`;
        }
    }

    /** Generate the mobile "date info" popover */
    getMobileDetail(noDatesForMonth) {
        if (!this.selectedDay) {
            if (noDatesForMonth) {
                return html`<div class="meal-calendar-info-box unselected">There are no available dates this&nbsp;month</div>`;
            } else {
                return html`<div class="meal-calendar-info-box unselected">Tap a highlighted date for more&nbsp;info</div>`;
            }
        }
        let conditionalContent;
        if (this.selectedDay.status === "booked" || this.selectedDay.status === "partial") {
            conditionalContent = html`
                <div class="activity-row">
                    <user-avatar avatar=${this.selectedDay.scheduledDates[0].Entity.AssignedUser.Avatar} initials=${this.selectedDay.scheduledDates[0].Entity.AssignedUser.Initials}></user-avatar>
                    <div>
                        <p class="activity-row__title">${this.selectedDay.scheduledDates[0].Entity.AssignedUser.FullName}</p>
                        <p class="activity-row__data">${this.selectedDay.scheduledDates[0].Entity.Description}</p>
                    </div>
                </div>
                ${this.selectedDay.scheduledDates[0].Entity.Notes ? html`<p class="meal-calendar-info-box__notes"><text-revealer cutoff="120" text="${this.selectedDay.scheduledDates[0].Entity.Notes}"></text-revealer></p>` : null}
                <div class="meal-calendar-info-box__actions">
                    ${GetActionButtons(this.selectedDay.scheduledDates[0].Entity)}
                </div>
            `;
        } else {
            conditionalContent = html`
                <div class="meal-calendar-info-box__available">
                    <p>Available</p>
                </div>
                <a class="button button-rectangle button-teal volunteer-button" href="/trains/${window.CurrentTrain.Id}/volunteer/${this.selectedDay.scheduledDates[0].Entity.Id}">Volunteer</a>
            `;
        }
        return html`
            <div class="meal-calendar-info-box"
                ?data-can-edit-booking=${this.selectedDay.scheduledDates[0].CanAdminister && this.selectedDay.scheduledDates[0] && !IsDateInPast(this.selectedDay.scheduledDates[0].Entity.Date)}
                ?data-is-own-booking=${this.selectedDay.scheduledDates[0] && this.selectedDay.scheduledDates[0].Entity.AssignedUser && this.selectedDay.scheduledDates[0].Entity.AssignedUser.Id === window.UserHash}>
                <div class="meal-calendar-info-box__header-row">
                    <div>
                        <h3 class="font:label-m"><span class="hide-visually">Selected Date:</span>${IsoDateFormatter.LongDate(this.selectedDay.date)}</h3>
                        <p class="weekday">${IsoDateFormatter.LongWeekday(this.selectedDay.date)}</p>
                    </div>
                    <button @click=${() => { this.closeMobileDetail(); }} class="close invisible-button">
                        <svg role="img" height="15px" width="15px" aria-label="Close">
                            <use xlink:href="#close-small" />
                        </svg>
                    </button>
                </div>
                ${conditionalContent}
            </div>
        `;
    }

    /** Generate the mobile "date info" popover for a plus train */
    getMobileDetailPlus(noDatesForMonth) {
        if (!this.selectedDay) {
            if (noDatesForMonth) {
                return html`<div class="meal-calendar-info-box unselected">There are no available dates this&nbsp;month</div>`;
            } else {
                return html`<div class="meal-calendar-info-box unselected">Tap a highlighted date for more&nbsp;info</div>`;
            }
        }
        const nodes = [];
        for (const schedule of this.selectedDay.scheduledDates) {
            nodes.push(html`<meal-date-row .trainDate=${schedule}></meal-date-row>`);
        }
        return html`
            <div class="meal-calendar-info-box" data-multiple-per-day="true">
                <div class="meal-calendar-info-box__header-row">
                    <div>
                        <h3 class="font:label-m"><span class="hide-visually">Selected Date:</span>${IsoDateFormatter.LongDate(this.selectedDay.date)}${this.selectedDay.status === "booked" ? html` <span class="tag-badge tag-badge-grey">Fully Booked</span>` : null}</h3>
                        <p class="font:size-xs">${IsoDateFormatter.LongWeekday(this.selectedDay.date)}</p>
                    </div>
                    <button @click=${() => { this.closeMobileDetail(); }} class="close invisible-button">
                        <svg role="img" height="15px" width="15px" aria-label="Close">
                            <use xlink:href="#close-small" />
                        </svg>
                    </button>
                </div>
                ${nodes}
            </div>
        `;
    }

    /** Generate the bootstrap modal for a "booked" date on desktop */
    getDesktopModal() {
        if (!this.showingModalDay) return null;
        // Note that this modal won't display until bootstrap is told to in javascript - we'll do that post render in updated()
        return html`
            <div id="calendarDayModal" class="modal fade calendar-detail-modal">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h1 class="modal-title">Meal Details</h1>
                            <button class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <div>
                                <h2>Date</h2>
                                <p>${IsoDateFormatter.LongestDate(this.showingModalDay.date)}</p>
                            </div>
                            <div>
                                <h2>Volunteer</h2>
                                <div class="activity-row">
                                    <user-avatar avatar=${this.showingModalDay.scheduledDates[0].Entity.AssignedUser.Avatar} initials=${this.showingModalDay.scheduledDates[0].Entity.AssignedUser.Initials}></user-avatar>
                                    <p class="activity-row__title">${this.showingModalDay.scheduledDates[0].Entity.AssignedUser.FullName}</p>
                                    ${this.showingModalDay.scheduledDates[0].Entity.AssignedUser.Id !== window.UserHash ? html`
                                        <div class="activity-row__actions">
                                            <a class="button button-rectangle button-outline button-condensed" href="/trains/${window.CurrentTrain.Id}/message/${this.showingModalDay.scheduledDates[0].Entity.AssignedUser.Id}"><svg role="img" width="24" height="24" aria-label="Message"><use xlink:href="#envelope-blue" /></svg><span class="text">Message</span></a>
                                        </div>
                                    ` : null}
                                </div>
                            </div>
                            <div>
                                <h2>Description</h2>
                                <p>${this.showingModalDay.scheduledDates[0].Entity.Description}</p>
                            </div>
                            ${this.showingModalDay.scheduledDates[0].Entity.Notes ? html`
                                <div>
                                    <h2>Notes</h2>
                                    <p>${this.showingModalDay.scheduledDates[0].Entity.Notes}</p>
                                </div>
                            ` : null}
                            ${((this.showingModalDay.scheduledDates[0].CanAdminister && !IsDateInPast(this.showingModalDay.date)) || window.CurrentTrain.IsAdmin) ? html`
                                <div class="modal-button-row">
                                    <button class="button button-rectangle button-outline" data-bs-dismiss="modal">Close</button>
                                    <a href="/trains/${window.CurrentTrain.Id}/volunteer/${this.showingModalDay.scheduledDates[0].Entity.Id}" class="button button-rectangle button-blue">
                                        <svg role="img" width="24" height="24" aria-label="Edit"><use xlink:href="#edit-white" /></svg>Edit
                                    </a>
                                </div>
                            ` : null}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /** Generate the bootstrap modal for a "booked" date on desktop for a plus train */
    getDesktopModalPlus() {
        if (!this.showingModalDay) return null;

        const nodes = [];
        for (const schedule of this.showingModalDay.scheduledDates) {
            nodes.push(html`<meal-date-row .trainDate=${schedule}></meal-date-row>`);
        }

        // Note that this modal won't display until bootstrap is told to in javascript - we'll do that post render in updated()
        return html`
            <div id="calendarDayModal" class="modal fade calendar-detail-modal" data-multiple-per-day="true">
                <div class="modal-dialog modal-dialog-scrollable modal-fullscreen-md-down modal-lg">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h1 class="modal-title">Requests for ${IsoDateFormatter.LongestDate(this.showingModalDay.date)}</h1>
                            <button class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <div class="calendar-detail-modal__list calendar-list" data-observe-width="600">
                            ${nodes}
                            </div>
                            <div class="modal-body-footer">
                                <button type="button" class="button button-outline" data-bs-dismiss="modal">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        if (!this.dates) return html`Loading`;
        /** @type {{ Entity: any, CanAdminister: boolean }[]} */
        const trainDatesForMonth = this.dates.filter(d => AreMonthsEqual(d.Entity.Date, this.startDate));
        const calendarDays = [];
        let noDatesForMonth = true;
        for (const dateIterator = new Date(this.startDate.getTime()); dateIterator.getMonth() === this.startDate.getMonth(); dateIterator.setDate(dateIterator.getDate() + 1)) {
            const scheduled = trainDatesForMonth.filter(item => parseISO(item.Entity.Date).getDate() === dateIterator.getDate());
            if (scheduled.length) noDatesForMonth = false;
            const fullyBooked = scheduled.every(s => s.Entity && s.Entity.AssignedUser && s.Entity.AssignedUser.Id);
            const partiallyBooked = scheduled.some(s => s.Entity && s.Entity.AssignedUser && s.Entity.AssignedUser.Id);
            const day = {
                date: new Date(dateIterator.getTime()),
                daynum: dateIterator.getDate(),
                scheduledDates: scheduled,
                isPast: IsDateInPast(dateIterator),
                status: scheduled.length ? (fullyBooked ? "booked" : (partiallyBooked ? "partial-booked" : "available")) : "",
                isSelected: this.selectedDay && this.selectedDay.date && (this.selectedDay.date.getTime() === dateIterator.getTime()),
            };
            calendarDays.push(day);
        }
        const startDayOfWeek = this.startDate.getDay();
        const dayHeaderLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

        return html`
            <div class="meal-calendar-legend"><span style="--this-color:var(--slate);">${window.CurrentTrain.IsPlus ? "Fully " : null}Booked</span><span style="--this-color:var(--brand-orange);">Available</span></div>
                <div class="meal-calendar-controls">
                    <div class="meal-calendar-controls__month">
                        <button @click=${() => this.changeMonth(false)} class="invisible-button" style="transform:rotateZ(180deg);">
                            <svg role="img" height="16" width="9" aria-label="Previous Month">
                                <use xlink:href="#chevron-right" />
                            </svg>
                        </button>
                        <div>${IsoDateFormatter.LongMonthYear(this.startDate)}</div>
                        <button @click=${() => this.changeMonth(true)} class="invisible-button">
                            <svg role="img" height="16" width="9" aria-label="Next Month">
                                <use xlink:href="#chevron-right" />
                            </svg>
                        </button>
                    </div>
                    <div class="meal-calendar-controls__today">
                        <button @click=${this.changeMonthToday} class="button button-link">Today</button>
                    </div>
                </div>
                <div class="meal-calendar-header">
                    ${dayHeaderLabels.map(day => html`<span class="meal-calendar__day-label" aria-hidden>${day}</span>`)}
                </div>
                <div class="meal-calendar" data-day-start="${startDayOfWeek}" data-multiple-per-day=${window.CurrentTrain.IsPlus}>
                    ${calendarDays.map(day => html`
                        <div class="meal-calendar__day" data-day-of-month=${day.daynum} ?data-past=${day.isPast} ?data-selected=${day.isSelected} data-status=${day.status}>
                            ${this.getMobileDayContent(day)}
                            ${this.getDesktopDayContent(day)}
                        </div>
                    `)}
                </div>
                ${window.CurrentTrain.IsPlus ? this.getMobileDetailPlus(noDatesForMonth) : this.getMobileDetail(noDatesForMonth)}
                ${window.CurrentTrain.IsPlus ? this.getDesktopModalPlus() : this.getDesktopModal()}
            </div>
        `;
    }

    // We have to show the bootstrap modal if we made one in render()
    updated() {
        const modalDiv = document.getElementById("calendarDayModal");
        if (modalDiv) {
            const modal = BootstrapModal.getOrCreateInstance(modalDiv);
            modal.show();
            // When the modal is hidden, turn off showingModalDay so we don't reshow the modal
            modalDiv.addEventListener("hidden.bs.modal", () => { this.showingModalDay = undefined; });
        }
        // Need to rescan width observers because the rendered DOM can have them (desktop plus modal)
        WidthObserver.PerformWireup();
    }
}
